import { inject, Injectable } from '@angular/core';
import { AuthenticationService } from '@auth/services/authentication.service';
import { Authority, UserRole } from '@openapi';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AuthorityService {
  private authenticationService = inject(AuthenticationService);

  /**
   * Updates a map of authority flags based on the current user's roles.
   *
   * Iterates through the user's roles and their authorities to set
   * boolean flags in the `authoritiesToCheck` map to `true` if present.
   *
   * @param authoritiesToCheck - A record of authority keys to be updated (e.g., ADMIN_READ ).
   * @returns Observable with the updated authority flags.
   */
  updateAuthorities(
    authoritiesToCheck: Record<string, boolean>
  ): Observable<Record<string, boolean>> {
    return this.authenticationService.getUserRoles().pipe(
      switchMap((userRoles: UserRole[]) => {
        // Flatten all authorities from user roles
        const userAuthorities = userRoles.reduce<Authority[]>(
          (authorities, role) => {
            if (role.authorities) {
              authorities.push(
                ...role.authorities.map(auth => ({ authority: auth.authority }))
              );
            }
            return authorities;
          },
          []
        );

        if (userAuthorities.length > 0) {
          const authoritySet = new Set(
            userAuthorities.map(auth => auth.authority)
          );

          // Enable authority flags if the user has them
          for (const authority in authoritiesToCheck) {
            if (authoritySet.has(authority)) {
              authoritiesToCheck[authority] = true;
            }
          }
        }

        return of(authoritiesToCheck);
      })
    );
  }
}
